import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Crown, Swords, BookOpen } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2M PR 1 — Inline Story Card.
 *
 * Embeds an editorial story (recap / retrospective / preview) inline on the
 * tournament detail + season detail pages.
 *
 *   <ArenaxStoryInline tournamentId="..." kind="match_recap" />
 *   <ArenaxStoryInline tournamentId="..." kind="season_retrospective" />
 *
 * Renders nothing when no story exists for that target — preserves the
 * "earned narrative" principle from the style guide.
 */
export default function ArenaxStoryInline({ tournamentId, matchId, kind = 'season_retrospective', variant = 'card' }) {
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url = null;
    if (kind === 'match_recap' && matchId) {
      url = `/arenax/matches/${matchId}/recap-story`;
    } else if (kind === 'season_retrospective' && tournamentId) {
      url = `/arenax/tournaments/${tournamentId}/retrospective`;
    }
    if (!url) { setLoading(false); return; }
    api.get(url)
      .then(r => setStory(r.data?.story || null))
      .catch(() => setStory(null))
      .finally(() => setLoading(false));
  }, [tournamentId, matchId, kind]);

  if (loading || !story) return null;

  const Icon = kind === 'season_retrospective' ? Crown
                : kind === 'match_recap' ? Swords : Flame;
  const accent = kind === 'season_retrospective' ? 'text-amber-100'
                  : kind === 'match_recap' ? 'text-amber-200' : 'text-rose-200';
  const border = kind === 'season_retrospective' ? 'border-amber-500/45'
                  : kind === 'match_recap' ? 'border-amber-500/35' : 'border-rose-500/35';
  const bg = kind === 'season_retrospective'
                ? 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(244,63,94,0.06), var(--ax-card-bg-soft))'
                : 'linear-gradient(135deg, rgba(245,158,11,0.08), var(--ax-card-bg-soft))';

  return (
    <div className={`rounded-2xl border ${border} p-5`}
      data-testid={`arenax-story-inline-${story.id}`}
      style={{ background: bg }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${accent}`} />
        <span className={`text-[10px] uppercase tracking-[0.3em] font-black ${accent}`}>
          {kind === 'season_retrospective' ? 'Retrospective' :
            kind === 'match_recap' ? 'Match Recap' : 'Preview'}
        </span>
      </div>
      <h3 className="text-lg sm:text-xl font-black text-slate-100 leading-tight mb-2"
        data-testid={`arenax-story-inline-headline-${story.id}`}>
        {story.headline}
      </h3>
      <p className="text-sm text-slate-200 leading-snug whitespace-pre-line">
        {story.body || (story.story || '').split('\n\n').slice(1).join('\n\n')}
      </p>
      <div className="mt-3 flex items-center justify-end">
        <Link to="/arenax/stories" className={`text-[10px] uppercase tracking-wider font-bold ${accent} hover:underline`}>
          More stories →
        </Link>
      </div>
    </div>
  );
}
